import {ForecastMapConfig} from '../../api/types';

/**
 * Interactive-overlay eligibility for the model+variable picker (#48).
 *
 * The authoritative signal is the backend-derived `config.interactive`
 * (a config renders a draped overlay iff it has exactly one paintable layer
 * on a regular lat/lon grid, or on a curvilinear grid the backend can regrid).
 * Neither is derivable client-side — curvilinear models (HRRR/RRFS/EC_RDPS)
 * render an overlay only when the backend ships a regrid asset for their grid
 * (backend `forecast_map/regrid_registry.py`, #107) — which is why the flag
 * comes from the API.
 *
 * Until that field is deployed, `interactive` is absent on every payload; we
 * then fall back to the half of the predicate the client *can* see (a single
 * paintable layer). The fallback intentionally over-includes curvilinear
 * configs — they simply fall back to the flipbook when selected — and
 * self-corrects the moment the backend field lands.
 */

// Scalar (filled_contour/contour/raster) + magnitude layers carry color and
// so define the overlay; barbs / vector fields are line art that ride on top.
const PAINTABLE_LAYER_TYPES = new Set([
  'filled_contour',
  'contour',
  'raster',
  'magnitude',
]);

const isSinglePaintable = (config: ForecastMapConfig): boolean =>
  config.parameters.layers.filter((layer) =>
    PAINTABLE_LAYER_TYPES.has(layer.layer_type),
  ).length === 1;

/**
 * The interactive-overlay predicate for one catalog: when the API knows
 * `interactive` (any config carries it), trust it; otherwise best-effort
 * derive it from a single paintable layer. Decided per catalog, so a payload
 * from before the field shipped is judged consistently.
 */
export const interactivePredicate = (
  configs: readonly ForecastMapConfig[],
): ((config: ForecastMapConfig) => boolean) => {
  const backendKnowsInteractive = configs.some(
    (config) => config.interactive !== undefined,
  );
  return (config) =>
    backendKnowsInteractive
      ? config.interactive === true
      : isSinglePaintable(config);
};

/** A config drawn over the model's whole grid, not cropped to a zone. */
export const isFullDomain = (config: ForecastMapConfig): boolean =>
  config.parameters.extent === null || config.parameters.extent === undefined;

/**
 * Whether one config opens on the interactive map: full domain and
 * interactive by the catalog's predicate. Presets and the user's own builder
 * configs alike.
 *
 * There is no dedupe: a coarser mesh of a variable the catalog also offers
 * at a finer one is still interactive. Decides where a
 * `/forecast-maps?config=` deep link goes (#327 MR5b).
 */
export const isInteractiveForecastConfig = (
  config: ForecastMapConfig,
  catalog: readonly ForecastMapConfig[],
): boolean => isFullDomain(config) && interactivePredicate(catalog)(config);
